import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { Avatar } from '@/components/brand/Brand';
import { Button, ButtonLink } from '@/components/ui/Button';
import { CourseTexture, CourseThumb } from '@/components/ui/CourseTexture';
import { EmptyState, Skeleton } from '@/components/ui/Feedback';
import { SectionBar } from '@/components/ui/SectionBar';
import { StatBlock, StatRow } from '@/components/ui/StatBlock';
import {
  statsEligibleRounds,
  courseNameFrom,
  inProgressRounds,
  useAllRounds,
  useCourseIndex,
  useHandicap,
  useRound,
  useRoundDetails,
} from '@/lib/api/queries';
import type { CourseSummary, RoundSummary } from '@/lib/api/types';
import { useCurrentUser } from '@/lib/auth/AuthContext';
import { cx } from '@/lib/cx';
import { fmtDateShort, fmtDateTime, fmtStartedAt } from '@/lib/format/date';
import { fmtDecimal, fmtInt, fmtPct, fmtToPar } from '@/lib/format/number';
import {
  MIN_ROUNDS_FOR_HANDICAP,
  roundsUntilHandicap,
  summariseRounds,
  withDetailStats,
} from '@/lib/stats/aggregate';
import { t } from '@/lib/i18n';
import { routes } from '@/routes/paths';
import styles from './StatsSummary.module.css';
import { ChevronRightIcon } from '@/components/icons';

export function StatsSummary() {
  const user = useCurrentUser();
  const navigate = useNavigate();

  const handicap = useHandicap();
  const allRounds = useAllRounds();
  const courseIndex = useCourseIndex();

  const rounds = allRounds.data?.rounds ?? [];
  const completed = useMemo(() => statsEligibleRounds(rounds), [rounds]);
  const active = useMemo(() => inProgressRounds(rounds), [rounds]);

  const latest = completed[0];
  const latestDetail = useRound(latest?.id);

  // Detail for the recent rounds only — enough for "Seu jogo" without walking the whole
  // history. The full walk belongs to Estatísticas, and shares this cache.
  const recentIds = useMemo(() => completed.slice(0, 10).map((r) => r.id), [completed]);
  const details = useRoundDetails(recentIds);

  const summary = useMemo(
    () => withDetailStats(summariseRounds(completed), details.rounds),
    [completed, details.rounds],
  );

  const homeCourse = user.homeCourseId ? courseIndex.data?.get(user.homeCourseId) : undefined;
  const recentCourses = useRecentCourses(completed, courseIndex.data, user.homeCourseId);

  const loading = allRounds.isLoading || handicap.isLoading;
  const activeRound = active[0];

  if (loading) return <StatsSummarySkeleton name={user.displayName} />;

  const hasRounds = completed.length > 0 || active.length > 0;

  return (
    <div className={styles.page}>
      {activeRound ? (
        <>
          <CourseTexture
            location={null}
            veil="none"
            className={cx(styles.hero, styles.heroBarOnly)}
          >
            <TopBar name={user.displayName} />
          </CourseTexture>

          <InProgressBlock
            round={activeRound}
            courseName={courseNameFrom(courseIndex.data, activeRound.courseId)}
            onContinue={() => navigate(routes.round(activeRound.id))}
          />

          <section className={styles.handicapPanel}>
            <HandicapReadout
              index={handicap.data?.handicapIndex ?? null}
              basedOn={handicap.data?.basedOnRounds ?? 0}
              lastUpdated={handicap.data?.lastUpdated}
              completedCount={completed.length}
            />
          </section>
        </>
      ) : (
        <CourseTexture location={homeCourse?.location} veil="header" className={styles.hero}>
          <TopBar name={user.displayName} />
          <div className={styles.handicapBlock}>
            <HandicapReadout
              onInk
              index={handicap.data?.handicapIndex ?? null}
              basedOn={handicap.data?.basedOnRounds ?? 0}
              lastUpdated={handicap.data?.lastUpdated}
              completedCount={completed.length}
            />
          </div>
        </CourseTexture>
      )}

      {latest ? (
        <>
          <SectionBar
            variant="mid"
            aside={<Link to={routes.rounds}>{t('home.seeAll')}</Link>}
            asideIsAction
          >
            {activeRound ? t('home.lastCompleted') : t('home.lastRound')}
          </SectionBar>

          <Link to={routes.round(latest.id)} className={styles.card}>
            <div className={styles.cardHead}>
              <div>
                <div className={styles.cardCourse}>
                  {courseNameFrom(courseIndex.data, latest.courseId)}
                </div>
                <div className={styles.cardMeta}>
                  {t('home.cardMeta', {
                    date: fmtDateShort(latest.completedAt ?? latest.startedAt),
                    holes: latest.holesPlayed,
                    tee: latest.teeBoxName,
                  })}
                </div>
              </div>
              <div className={styles.cardScore}>
                <div className={styles.cardScoreValue}>{fmtInt(latest.totalStrokes)}</div>
                <div className={styles.cardScoreToPar}>{fmtToPar(latest.totalToPar)}</div>
              </div>
            </div>

            <StatRow className={styles.cardStats}>
              <StatBlock
                label={t('round.putts')}
                value={fmtInt(latestDetail.data?.stats.totalPutts ?? null)}
              />
              <StatBlock
                label={t('round.gir')}
                value={
                  latestDetail.data ? fmtPct(latestDetail.data.stats.greensInRegulationPct) : null
                }
              />
              <StatBlock
                label="FW"
                value={latestDetail.data ? fmtPct(latestDetail.data.stats.fairwayHitPct) : null}
              />
              <StatBlock label={t('rounds.table.sd')} value={fmtDecimal(latest.scoreDifferential)} />
            </StatRow>
          </Link>

          <SectionBar aside={t('rounds.count', { count: completed.length })}>
            {t('home.yourGame')}
          </SectionBar>
          <div className={styles.gameGrid}>
            <div className={styles.gameCell}>
              <StatBlock label={t('stats.averageScore')} value={fmtDecimal(summary.averageScore)} />
            </div>
            <div className={styles.gameCell}>
              <StatBlock label={t('stats.bestRound')} value={fmtInt(summary.bestScore)} />
            </div>
            <div className={styles.gameCell}>
              <StatBlock
                label={t('stats.averageGir')}
                value={summary.averageGirPct == null ? null : fmtPct(summary.averageGirPct)}
                pct={summary.averageGirPct}
                caption={details.isLoading ? t('home.calculating') : undefined}
              />
            </div>
            <div className={styles.gameCell}>
              <StatBlock
                label={t('stats.puttsPerRound')}
                value={fmtDecimal(summary.averagePuttsPerRound)}
                caption={details.isLoading ? t('home.calculating') : undefined}
              />
            </div>
          </div>
        </>
      ) : (
        !activeRound && (
          <EmptyState
            title={t('home.empty.title')}
            actions={
              <>
                <ButtonLink to={routes.record} size="lg" block>
                  {t('rounds.record')}
                </ButtonLink>
                <ButtonLink to={routes.courses} variant="secondary" size="lg" block>
                  {t('home.exploreCourses')}
                </ButtonLink>
              </>
            }
          >
            {t('home.empty.body')}
          </EmptyState>
        )
      )}

      {recentCourses.length > 0 && (
        <>
          <SectionBar>{t('nav.courses')}</SectionBar>
          {recentCourses.map((c) => (
            <Link key={c.id} to={routes.course(c.id)} className={styles.courseRow}>
              <CourseThumb location={c.location} size={44} />
              <span className={styles.courseGrow}>
                <span className={styles.courseName}>{c.name}</span>
                <span className={styles.courseMeta}>
                  {[c.city, c.id === user.homeCourseId ? t('courses.homeTag') : null]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span className={styles.chevron} aria-hidden="true">
                <ChevronRightIcon size={16} />
              </span>
            </Link>
          ))}
        </>
      )}

      {!hasRounds && recentCourses.length === 0 && null}
    </div>
  );
}

/**
 * Cabeçalho do 1c. O wordmark saiu daqui quando esta tela deixou de ser a primeira aba: quem
 * chega já sabe em que app está, e o que falta dizer é em que tela. A cápsula "Detalhados ▸"
 * é a única porta para os gráficos (1l/1o) — sem ela eles ficariam sem endereço na navegação.
 */
function TopBar({ name }: { name: string }) {
  return (
    <div className={styles.heroBar}>
      <span className={styles.heroTitle}>{t('home.title')}</span>
      <span className={styles.heroActions}>
        <Link to={routes.statsDetailed} className={styles.detailsPill}>
          {t('home.detailed')}
        </Link>
        <Link to={routes.profile} className={styles.heroAvatar} aria-label={t('nav.aria.profile')}>
          <Avatar name={name} size={34} />
        </Link>
      </span>
    </div>
  );
}

interface HandicapReadoutProps {
  index: number | null;
  basedOn: number;
  lastUpdated?: string;
  completedCount: number;
  onInk?: boolean;
}

/**
 * The handicap index, or the countdown to having one.
 *
 * WHS produces nothing below three completed rounds, and the backend returns null there.
 * Showing "—" alone would read as an error; showing how many rounds are left turns it into
 * a goal, which is what the canvas does with the three-segment bar.
 */
function HandicapReadout({ index, basedOn, lastUpdated, completedCount }: HandicapReadoutProps) {
  const missing = roundsUntilHandicap(completedCount);

  return (
    <div>
      <div className={styles.eyebrow}>{t('home.handicapIndex')}</div>

      {index != null ? (
        <>
          <div className={styles.handicapRow}>
            <span className={styles.handicapValue}>{fmtDecimal(index)}</span>
          </div>
          <div className={styles.handicapMeta}>
            {lastUpdated
              ? t('home.updatedAt', {
                  base: t('home.basedOn', { count: basedOn }),
                  date: fmtDateShort(lastUpdated),
                })
              : t('home.basedOn', { count: basedOn })}
          </div>
        </>
      ) : (
        <>
          <div className={styles.handicapRow}>
            <span className={cx(styles.handicapValue, styles.handicapEmpty)}>—</span>
          </div>
          <div className={styles.handicapMeta}>
            {missing === 0
              ? t('home.handicapNext')
              : t('home.handicapMissing', { count: missing })}
          </div>
          <div className={styles.progressSegments} aria-hidden="true">
            {Array.from({ length: MIN_ROUNDS_FOR_HANDICAP }, (_, i) => (
              <span
                key={i}
                className={cx(styles.segment, i < completedCount && styles.segmentDone)}
              />
            ))}
          </div>
          <div className={styles.progressCaption}>
            {t('home.handicapProgress', {
              done: completedCount,
              total: MIN_ROUNDS_FOR_HANDICAP,
            })}
          </div>
        </>
      )}
      {lastUpdated && index != null && (
        <span className="visually-hidden">
          {t('home.updatedOn')} {fmtDateTime(lastUpdated)}
        </span>
      )}
    </div>
  );
}

function InProgressBlock({
  round,
  courseName,
  onContinue,
}: {
  round: RoundSummary;
  courseName: string;
  onContinue: () => void;
}) {
  // Summaries carry no scores array, so "how far in" is not knowable from the list alone.
  const detail = useRound(round.id);
  const scored = detail.data?.stats.holesScored ?? null;
  const pct = scored == null ? 0 : (scored / round.holesPlayed) * 100;

  return (
    <section className={styles.inProgress}>
      <div className={styles.inProgressTag}>
        <span className={styles.inProgressDot} aria-hidden="true" />
        <span className={styles.inProgressLabel}>{t('home.inProgress')}</span>
      </div>

      <h2 className={styles.inProgressCourse}>{courseName}</h2>

      <div className={styles.inProgressMeta}>
        {t('home.startedAt', { when: fmtStartedAt(round.startedAt), tee: round.teeBoxName })}
      </div>

      <div className={styles.progressRow}>
        <div className={styles.progressTrack}>
          <div className={styles.progressFill} style={{ width: `${pct}%` }} />
        </div>
        <span className={styles.progressCount}>
          {scored == null ? '—' : scored}{' '}
          <span className={styles.progressCountTotal}>
            {t('home.ofTotal', { total: round.holesPlayed })}
          </span>
        </span>
      </div>

      <div className={styles.inProgressActions}>
        <Button size="lg" onClick={onContinue}>
          {t('rounds.continue')}
        </Button>
        <ButtonLink to={routes.round(round.id)} variant="secondary" size="lg">
          {t('home.viewRound')}
        </ButtonLink>
      </div>

      <p className={styles.inProgressNote}>{t('home.unfinishedNote')}</p>
    </section>
  );
}

/** Courses the player has actually played, most recent first, home course pinned. */
function useRecentCourses(
  completed: RoundSummary[],
  index: Map<string, CourseSummary> | undefined,
  homeCourseId: string | null,
): CourseSummary[] {
  return useMemo(() => {
    if (!index) return [];
    const seen = new Set<string>();
    const out: CourseSummary[] = [];

    const home = homeCourseId ? index.get(homeCourseId) : undefined;
    if (home) {
      out.push(home);
      seen.add(home.id);
    }

    for (const r of completed) {
      if (seen.has(r.courseId)) continue;
      const course = index.get(r.courseId);
      if (!course) continue;
      out.push(course);
      seen.add(course.id);
      if (out.length >= 4) break;
    }
    return out;
  }, [completed, index, homeCourseId]);
}

function StatsSummarySkeleton({ name }: { name: string }) {
  return (
    <div className={styles.page}>
      <CourseTexture location={null} veil="none" className={styles.hero}>
        <TopBar name={name} />
        <div className={styles.handicapBlock}>
          <Skeleton width="40%" height={11} style={{ opacity: 0.25 }} />
          <Skeleton width="55%" height={56} style={{ marginTop: 10, opacity: 0.25 }} />
        </div>
      </CourseTexture>
      <div style={{ padding: 'var(--space-6) var(--gutter)' }}>
        <Skeleton width="70%" height={22} />
        <Skeleton width="45%" height={12} style={{ marginTop: 10 }} />
        <div style={{ display: 'flex', gap: 16, marginTop: 24 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ flex: 1 }}>
              <Skeleton height={9} />
              <Skeleton height={24} style={{ marginTop: 8 }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
